import { getCachedTokenManager } from "@api/use-cached-token-provider";
// eslint-disable-next-line no-restricted-imports -- Using the type only, not constructing it.
import type { CoreApiTokenManager } from "@faro-lotv/service-wires";
import { useAppParams } from "@router/router-helper";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import {
  UseTrackEvent,
  useTrackEvent,
} from "@utils/track-event/use-track-event";
import { isNumber } from "es-toolkit";
import { useEffect } from "react";

/** After the first token request after page load, we track some metrics. */
let hasTrackedTokenMetrics = false;

/**
 * Round the clock accuracy to buckets for easier grouping in Amplitude.
 * @returns E.g. 30 if `seconds` is <= 30, but bigger than the next-smaller bucket threshold.
 */
export function roundClockAccuracy(seconds: number): number {
  seconds = Math.round(seconds);

  /* eslint-disable @typescript-eslint/no-magic-numbers -- These are the buckets we want to use. */
  if (Math.abs(seconds) <= 5) {
    return seconds;
  } else {
    // Round away from zero to the next-bigger multiple of `bucketSize`.
    const bucketSize = Math.abs(seconds) <= 60 ? 10 : 30;
    return seconds > 0
      ? bucketSize * Math.ceil(seconds / bucketSize)
      : bucketSize * Math.floor(seconds / bucketSize);
  }
  /* eslint-enable */
}

/** Track metrics about the first token request. */
function trackTokenMetrics(
  tokenManager: CoreApiTokenManager,
  trackEvent: UseTrackEvent["trackEvent"]
): void {
  if (
    hasTrackedTokenMetrics ||
    !isNumber(tokenManager.browserClockBehindCoreApi)
  ) {
    return;
  }

  // Round to make it easier to create a bucket for each value in Amplitude.
  const browserClockBehindCoreApi = Math.round(
    tokenManager.browserClockBehindCoreApi
  );
  const browserClockBehindCoreApiBucket = roundClockAccuracy(
    browserClockBehindCoreApi
  );
  hasTrackedTokenMetrics = true;

  trackEvent({
    name: ProjectEvents.fetchProjectToken,
    props: {
      browserClockBehindCoreApi,
      browserClockBehindCoreApiBucket,
    },
  });
}

/**
 * Track some metrics about the first CoreAPI project token request.
 */
export function useTrackTokenMetrics(): void {
  const { trackEvent } = useTrackEvent();
  const appParams = useAppParams();

  useEffect(() => {
    if (!hasTrackedTokenMetrics && appParams.projectId) {
      const tokenManager = getCachedTokenManager(appParams.projectId);
      // When loading a project, Dashboard needs a token anyways, so it doesn't harm if we request one here.
      tokenManager
        .getToken()
        .then(() => {
          trackTokenMetrics(tokenManager, trackEvent);
          // If fetching the token fails, other places will already handle the error.
        })
        .catch(() => undefined);
    }
  }, [appParams.projectId, trackEvent]);
}
